import { EnvironmentId } from '@/react/portainer/environments/types';
import {
  AutoUpdateResponse,
  RelativePathModel,
  RepoConfigResponse,
} from '@/react/portainer/gitops/types';
import { RegistryId } from '@/react/portainer/registries/types/registry';
import { StackDeploymentInfo } from '@/react/common/stacks/types';
import { makeGetEnumParam } from '@/react/utils/enum';

import { EnvVar } from '@@/form-components/EnvironmentVariablesFieldset/types';

import { EdgeGroup } from '../edge-groups/types';

import { type StaggerConfig } from './components/StaggerFieldset.types';
import { DeployerOptions } from './components/DeployerOptions';
import { KubernetesDeployerOptions } from './components/KubernetesDeployerOptions';

export {
  type StaggerConfig,
  StaggerOption,
  StaggerParallelOption,
  UpdateFailureAction,
} from './components/StaggerFieldset.types';

export enum StatusType {
  /** Pending represents a pending edge stack */
  Pending,
  /** DeploymentReceived represents an edge environment which received the edge stack deployment */
  DeploymentReceived,
  /** Error represents an edge environment which failed to deploy its edge stack */
  Error,
  /** Acknowledged represents an acknowledged edge stack */
  Acknowledged,
  /** Removed represents a removed edge stack */
  Removed,
  /** StatusRemoteUpdateSuccess represents a successfully updated edge stack */
  RemoteUpdateSuccess,
  /** ImagesPulled represents a successfully images-pulling */
  ImagesPulled,
  /** Running represents a running Edge stack */
  Running,
  /** Deploying represents an Edge stack which is being deployed */
  Deploying,
  /** Removing represents an Edge stack which is being removed */
  Removing,
  /** PausedDeploying represents an Edge stack which is paused for deployment */
  PausedDeploying,
  /** PausedRemoving represents an Edge stack which is being rolled back */
  RollingBack,
  /** PausedRemoving represents an Edge stack which has been rolled back */
  RolledBack,
  /** Completed represents a completed Edge stack */
  Completed,
}

export const getEdgeStackStatusType = makeGetEnumParam(StatusType);

export interface DeploymentStatus {
  Type: StatusType;
  Error: string;
  Time: number;
}

interface EdgeStackDeploymentInfo {
  Version: number;
  FileVersion: number;
  ConfigHash: string;
}

export interface EdgeStackStatus {
  Status: Array<DeploymentStatus>;
  EndpointID: EnvironmentId;
  DeploymentInfo?: EdgeStackDeploymentInfo;
}

export interface HelmConfig {
  /** Path to a Helm chart folder for Git-based deployments */
  ChartPath: string;
  /** URL of a Helm repository for repository-based deployments (mutually exclusive with ChartPath) */
  ChartURL?: string;
  /** Name of the Helm chart in the repository (required for repository-based deployments) */
  ChartName?: string;
  /** Version of the Helm chart to deploy (empty means latest) */
  ChartVersion?: string;
  /** Paths to values YAML files for Git-based deployments */
  ValuesFiles?: string[];
  /** Inline YAML values for repository-based deployments */
  ValuesInline?: string;
  Version?: string;
  Atomic: boolean;
  Timeout?: string;
  Namespace?: string;
}

export enum DeploymentType {
  /** represent an edge stack deployed using a compose file */
  Compose,
  /** represent an edge stack deployed using a kubernetes manifest file */
  Kubernetes,
  /** represent an edge stack deployed using a helm chart */
  KubernetesHelm = 3,
}

export const EDGE_STACK_ID_LABEL = 'io.portainer.edge_stack_id';

// eslint-disable-next-line no-template-curly-in-string
export const PORTAINER_EDGE_GROUP_VARIABLE = '${PORTAINER_EDGE_GROUP}';
// eslint-disable-next-line no-template-curly-in-string
export const PORTAINER_EDGE_ID_VARIABLE = '${PORTAINER_EDGE_ID}';

export type EdgeStack = RelativePathModel & {
  Id: number;
  Name: string;
  Status: { [key: EnvironmentId]: EdgeStackStatus };
  CreationDate: number;
  EdgeGroups: Array<EdgeGroup['Id']>;
  Registries: RegistryId[];
  ProjectPath: string;
  EntryPoint: string;
  Version: number;
  NumDeployments: number;
  ManifestPath: string;
  DeploymentType: DeploymentType;
  EdgeUpdateID: number;
  ScheduledTime: string;
  UseManifestNamespaces: boolean;
  PrePullImage: boolean;
  AutoUpdate?: AutoUpdateResponse;
  GitConfig?: RepoConfigResponse;
  GitSourceId?: number;
  WorkflowID?: number;
  Prune: boolean;
  RetryDeploy: boolean;
  RetryPeriod: number;
  Webhook: string;
  StackFileVersion?: number;
  PreviousDeploymentInfo: EdgeStackDeploymentInfo;
  CurrentDeploymentInfo?: StackDeploymentInfo;
  EnvVars?: EnvVar[];
  StaggerConfig?: StaggerConfig;
  SupportRelativePath: boolean;
  AlwaysCloneGitRepoForRelativePath: boolean;
  FilesystemPath?: string;
  DeployerOptions: DeployerOptions;
  KubernetesOptions?: KubernetesDeployerOptions;
  RepullImageAndRedeploy: boolean;
  HelmConfig?: HelmConfig;
};

export { DeploymentType as EditorType };
